import { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { NotificationCoordinator } from './NotificationCoordinator';
import { NotificationService } from '../../services/notifications/NotificationService';
import type { INotificationService } from '../../services/notifications/INotificationService';

export const NotificationCoordinatorContext = createContext<NotificationCoordinator | null>(null);

export function useNotificationCoordinator(): NotificationCoordinator {
  const coordinator = useContext(NotificationCoordinatorContext);
  if (!coordinator) throw new Error('useNotificationCoordinator must be used within NotificationCoordinatorContext');
  return coordinator;
}

/** Subscribes a component to the coordinator snapshot. */
export function useNotificationInbox() {
  const coordinator = useNotificationCoordinator();
  return useSyncExternalStore(
    (onStoreChange) => coordinator.subscribe(onStoreChange),
    () => coordinator.getSnapshot(),
  );
}

/**
 * Provider that owns one coordinator for the signed-in session. `service` is
 * injectable so tests can supply a fake.
 */
export function NotificationCoordinatorProvider({
  children,
  isNotificationsScreenMounted,
  service,
  onUnreadDecremented,
  onUnreadCountChanged,
}: {
  children: ReactNode;
  isNotificationsScreenMounted: () => boolean;
  service?: INotificationService;
  onUnreadDecremented?: () => void;
  onUnreadCountChanged?: (count: number) => void;
}) {
  const coordinator = useMemo(
    () =>
      new NotificationCoordinator({
        service: service ?? new NotificationService(),
        isNotificationsScreenMounted,
        onUnreadDecremented,
        onUnreadCountChanged,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    coordinator.start();
    return () => coordinator.stop();
  }, [coordinator]);

  return (
    <NotificationCoordinatorContext.Provider value={coordinator}>
      {children}
    </NotificationCoordinatorContext.Provider>
  );
}
